import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Sparkles, FileText, LayoutDashboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AppShell } from '../components/Layout';
import { billingApi, readPendingAudit } from '../lib/billing';

/**
 * BillingSuccessPage
 * Shown after a successful Razorpay payment + signature verification.
 *
 * Two cases:
 *   1. The user came here via the in-flight "audit-quota" flow (they had a
 *      pending wizard state saved before paying). We show a primary CTA to
 *      resume the audit \u2014 routes to /wizard?resume=1 which hydrates state
 *      and auto-submits.
 *   2. Otherwise (organic /pricing checkout), we show neutral CTAs to the
 *      dashboard and billing pages.
 */
export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [me, setMe] = useState(null);

  const nextHint = params.get('next');
  const pending = useMemo(() => readPendingAudit(), []);

  useEffect(() => {
    let active = true;
    billingApi.me().then((d) => { if (active) setMe(d); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const planLabel = (() => {
    if (!me) return 'Active plan';
    if (me.plan_type === 'one_time') return 'Profile Success Plan';
    if (me.plan_type === 'gold') return 'Executive Thought Leadership Plan';
    if (me.plan_type === 'comp') return 'Complimentary Access';
    return 'Active plan';
  })();

  const showResumeAudit = (nextHint === 'audit' || !!pending) && !!pending;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Card
          className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-[hsl(var(--mint))]/30 via-card to-card"
          data-testid="billing-success-card"
        >
          <CardContent className="p-7 sm:p-10 space-y-6">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shrink-0">
                <CheckCircle2 size={22} />
              </span>
              <div className="min-w-0">
                <Badge
                  variant="secondary"
                  className="font-mono-edge text-[10px] tracking-[0.18em]"
                  data-testid="billing-success-badge"
                >
                  PAYMENT SUCCESSFUL
                </Badge>
                <h1
                  className="font-display text-2xl sm:text-3xl tracking-[-0.01em] mt-1.5"
                  data-testid="billing-success-title"
                >
                  You&rsquo;re all set.
                </h1>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              We&rsquo;ve activated your{' '}
              <span className="font-medium text-foreground">{planLabel}</span>.
              Your access is live right now &mdash; no waiting on emails or webhooks.
            </p>

            {showResumeAudit ? (
              <div
                className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
                data-testid="billing-success-resume-block"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <Sparkles size={18} className="text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Resume your audit</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Your profile is already loaded &mdash; we&rsquo;ll start the audit
                      the moment you click.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/wizard?resume=1')}
                  className="rounded-xl shrink-0"
                  data-testid="billing-success-resume-cta"
                >
                  Start audit <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            ) : (
              <div
                className="rounded-xl border border-border bg-card/60 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
                data-testid="billing-success-default-block"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <LayoutDashboard size={18} className="text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Ready when you are</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Head to the dashboard to upload your LinkedIn profile and
                      run your first audit.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="rounded-xl shrink-0"
                  data-testid="billing-success-dashboard-cta"
                >
                  Go to dashboard <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            )}

            <div className="pt-2 border-t border-border flex flex-wrap items-center gap-3 justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5" data-testid="billing-success-same-email-note">
                <FileText size={12} /> Receipt sent by Razorpay to the email you paid with — keep using that email at login.
              </span>
              <button
                type="button"
                onClick={() => navigate('/billing')}
                className="underline-offset-4 hover:underline text-foreground/80 hover:text-foreground transition-colors"
                data-testid="billing-success-view-billing-link"
              >
                View billing &amp; usage &rarr;
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Made a mistake? You can request a refund within 24 hours &mdash; see{' '}
          <button
            type="button"
            onClick={() => navigate('/refund-policy')}
            className="underline-offset-4 hover:underline"
            data-testid="billing-success-refund-link"
          >
            our refund policy
          </button>
          .
        </p>
      </div>
    </AppShell>
  );
}
